/**
 * CallLog
 * -------
 * Internal, authenticated-only. Not on any public page.
 *
 * Renders (a) a mobile-friendly form to log a call against a program or an
 * alternative_use slug, and (b) an append-only history list of past calls
 * against that target, newest first. The history is what turns state-2
 * alternative-use cards and public-source program cards into partner-confirmed
 * program cards over time — one honest conversation at a time.
 *
 * Discipline:
 *   - Every call is its own record. We NEVER overwrite a prior call.
 *   - Save-time prompt asks whether to promote the target's evidence level,
 *     but the server never auto-promotes — the operator makes that call
 *     explicitly. For a program target, the promotion is written through.
 *     For an alternative_use target, the "suggested" flag is stored on the
 *     call itself and revisited later (the alt-use catalog is a versioned
 *     Python module, not a Mongo record).
 *   - Form is deliberately compact for one-handed post-call entry on a phone.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Phone, CalendarClock, PhoneCall, User2, ChevronDown, ChevronRight, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const QUESTIONS = [
  { key: "q1_payment_amount",    label: "1. What do you pay for a unit like this, and how is that number set?" },
  { key: "q2_check_flow",        label: "2. Who cuts the check, and on what schedule?" },
  { key: "q3_referral_control",  label: "3. Who controls the referral — you, or someone upstream?" },
  { key: "q4_property_pass",     label: "4. What does the property have to pass, and how long does the inspection take?" },
  { key: "q5_deal_breakers",     label: "5. What makes you say no to a property?" },
];

const EVIDENCE_LEVEL_LABEL = {
  assumption: "Assumption",
  public_source: "From a public source",
  needs_verification: "Needs verification",
  partner_confirmed: "Confirmed with the agency",
  verified_fact: "Verified fact",
};

function today() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const emptyForm = () => ({
  call_date: today(),
  contact_name: "", contact_title: "", contact_org: "",
  contact_phone: "", contact_email: "",
  q1_payment_amount: "", q2_check_flow: "", q3_referral_control: "",
  q4_property_pass: "", q5_deal_breakers: "",
  general_notes: "",
  follow_up_needed: false, follow_up_date: "",
  suggested_evidence_promotion: false,
});

export default function CallLog({ targetType, targetId, targetLabel, currentEvidenceLevel = "", onEvidencePromoted }) {
  const [form, setForm] = useState(emptyForm());
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [postSaveCall, setPostSaveCall] = useState(null); // { id, suggested_evidence_promotion }

  const listUrl = useMemo(() => (
    targetType === "program"
      ? `/admin/programs/${targetId}/calls`
      : `/admin/alternative-uses/${targetId}/calls`
  ), [targetType, targetId]);

  const postUrl = listUrl;

  const refresh = async () => {
    if (!targetId) return;
    setLoading(true);
    try {
      const r = await api.get(listUrl);
      setCalls(r.data.calls || []);
    } catch (e) {
      toast.error("Could not load call history");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [targetType, targetId]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(postUrl, form);
      const saved = r.data.call;
      setPostSaveCall(saved);
      setForm(emptyForm());
      // Prepend to history so the append-only log is visibly append-only.
      setCalls((c) => [saved, ...c]);
      toast.success("Call logged");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const promote = async (toLevel) => {
    if (targetType !== "program") {
      toast.info("Alternative-use promotions are captured in the call and revisited in code. No auto-promotion.");
      setPostSaveCall(null);
      return;
    }
    try {
      const r = await api.post(`/admin/programs/${targetId}/promote-evidence-level`, {
        to_level: toLevel,
        reason_call_id: postSaveCall?.id || "",
      });
      toast.success(`Evidence level: ${r.data.from} → ${r.data.to}`);
      setPostSaveCall(null);
      if (onEvidencePromoted) onEvidencePromoted(toLevel);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Promotion failed");
    }
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <Card className="p-4 md:p-5 border-slate-200" data-testid="call-log">
      <div className="flex items-center gap-2 text-slate-900 font-semibold mb-1">
        <PhoneCall className="w-4 h-4 text-[#C25E00]" /> Agency Call Log
      </div>
      <p className="text-xs text-slate-500 leading-relaxed">
        Every call to <span className="font-semibold text-slate-800">{targetLabel}</span> lands here. Append-only — old calls are never overwritten. A logged call is not a verified fact.
      </p>

      {/* Form */}
      <form onSubmit={submit} className="mt-4 space-y-3" data-testid="call-log-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Date of call</Label>
            <Input type="date" required value={form.call_date} onChange={(e) => setField("call_date", e.target.value)} data-testid="call-log-date" />
          </div>
          <div>
            <Label className="text-xs">Contact name</Label>
            <Input value={form.contact_name} onChange={(e) => setField("contact_name", e.target.value)} placeholder="Full name" data-testid="call-log-contact-name" />
          </div>
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={form.contact_title} onChange={(e) => setField("contact_title", e.target.value)} data-testid="call-log-contact-title" />
          </div>
          <div>
            <Label className="text-xs">Organization</Label>
            <Input value={form.contact_org} onChange={(e) => setField("contact_org", e.target.value)} data-testid="call-log-contact-org" />
          </div>
          <div>
            <Label className="text-xs">Direct phone</Label>
            <Input type="tel" inputMode="tel" value={form.contact_phone} onChange={(e) => setField("contact_phone", e.target.value)} data-testid="call-log-contact-phone" />
          </div>
          <div>
            <Label className="text-xs">Direct email</Label>
            <Input type="email" value={form.contact_email} onChange={(e) => setField("contact_email", e.target.value)} data-testid="call-log-contact-email" />
          </div>
        </div>

        <div className="pt-2 border-t border-slate-100 space-y-3">
          {QUESTIONS.map((q) => (
            <div key={q.key}>
              <Label className="text-xs font-semibold text-slate-800">{q.label}</Label>
              <Textarea
                rows={2}
                value={form[q.key]}
                onChange={(e) => setField(q.key, e.target.value)}
                placeholder="Their exact answer — verbatim helps later"
                data-testid={`call-log-${q.key}`}
              />
            </div>
          ))}
        </div>

        <div className="pt-2 border-t border-slate-100 space-y-3">
          <div>
            <Label className="text-xs">Other notes</Label>
            <Textarea rows={2} value={form.general_notes} onChange={(e) => setField("general_notes", e.target.value)} placeholder="Anything else worth remembering" data-testid="call-log-notes" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="call-log-followup-wrap">
              <input type="checkbox" checked={form.follow_up_needed} onChange={(e) => setField("follow_up_needed", e.target.checked)} data-testid="call-log-followup" />
              Follow-up needed
            </label>
            {form.follow_up_needed && (
              <div>
                <Label className="text-xs">By when</Label>
                <Input type="date" value={form.follow_up_date} onChange={(e) => setField("follow_up_date", e.target.value)} data-testid="call-log-followup-date" />
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="call-log-suggest-promote-wrap">
            <input type="checkbox" checked={form.suggested_evidence_promotion} onChange={(e) => setField("suggested_evidence_promotion", e.target.checked)} data-testid="call-log-suggest-promote" />
            After this call, I want to consider promoting the evidence level
          </label>
        </div>

        <div className="pt-2 flex flex-col sm:flex-row gap-2">
          <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 min-h-[44px] flex-1" data-testid="call-log-submit">
            {busy ? "Saving…" : "Save call"}
          </Button>
          <Button type="button" variant="outline" onClick={() => setForm(emptyForm())} className="min-h-[44px]" data-testid="call-log-reset">
            Reset
          </Button>
        </div>
      </form>

      {/* Post-save promotion prompt */}
      {postSaveCall && (
        <div className="mt-4 p-3 border-2 border-amber-300 bg-amber-50 rounded-md" data-testid="call-log-promote-prompt">
          <div className="flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-amber-900 leading-relaxed">
              <div className="font-semibold">Call saved. Promote evidence level?</div>
              <div className="text-xs mt-0.5">
                {targetType === "program"
                  ? <>Current: <span className="font-semibold">{EVIDENCE_LEVEL_LABEL[currentEvidenceLevel] || currentEvidenceLevel || "(unset)"}</span>. Only you decide — the server does not auto-promote.</>
                  : <>Alternative uses live in a versioned code catalog. Your suggestion is captured on this call and revisited in a code change — no on-the-fly promotion.</>}
              </div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {targetType === "program" && (
              <>
                <Button size="sm" onClick={() => promote("partner_confirmed")} className="bg-slate-900 hover:bg-slate-800" data-testid="call-log-promote-partner">Confirmed with the agency</Button>
                <Button size="sm" onClick={() => promote("verified_fact")} variant="outline" data-testid="call-log-promote-verified">Verified fact</Button>
              </>
            )}
            <Button size="sm" variant="ghost" onClick={() => setPostSaveCall(null)} data-testid="call-log-promote-skip">Not now</Button>
          </div>
        </div>
      )}

      {/* History */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Past calls · {calls.length}</div>
          {loading && <div className="text-xs text-slate-400">Loading…</div>}
        </div>
        {calls.length === 0 && !loading && (
          <div className="text-sm text-slate-500 py-3">No calls logged yet. Make one, learn one thing, log it.</div>
        )}
        <div className="space-y-2">
          {calls.map((c) => {
            const open = !!expanded[c.id];
            const nonEmpty = QUESTIONS.filter((q) => c.answers?.[q.key]).length;
            return (
              <Card key={c.id} className="border-slate-200 p-3" data-testid={`call-log-entry-${c.id}`}>
                <button type="button" onClick={() => toggle(c.id)} className="w-full text-left flex items-start gap-2">
                  <div className="mt-0.5 text-slate-400">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap text-sm">
                      <CalendarClock className="w-3.5 h-3.5 text-slate-400" />
                      <span className="font-semibold">{c.call_date || c.created_at?.slice(0, 10)}</span>
                      {c.contact?.name && <span className="text-slate-700 flex items-center gap-1"><User2 className="w-3 h-3" /> {c.contact.name}{c.contact.title ? `, ${c.contact.title}` : ""}</span>}
                      {c.contact?.org && <span className="text-slate-500">· {c.contact.org}</span>}
                      {c.follow_up_needed && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Follow-up{c.follow_up_date ? ` · ${c.follow_up_date}` : ""}</span>}
                      {c.suggested_evidence_promotion && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">Suggested promotion</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{nonEmpty} of 5 questions answered · logged by {c.author_email || "—"}</div>
                  </div>
                </button>
                {open && (
                  <div className="mt-3 pl-6 space-y-3 text-sm">
                    {(c.contact?.phone || c.contact?.email) && (
                      <div className="text-xs text-slate-600 flex items-center gap-3 flex-wrap">
                        {c.contact.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.contact.phone}</span>}
                        {c.contact.email && <span>{c.contact.email}</span>}
                      </div>
                    )}
                    {QUESTIONS.map((q) => (
                      c.answers?.[q.key] ? (
                        <div key={q.key}>
                          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{q.label}</div>
                          <div className="whitespace-pre-wrap text-slate-800 mt-0.5 leading-relaxed">{c.answers[q.key]}</div>
                        </div>
                      ) : null
                    ))}
                    {c.general_notes && (
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Other notes</div>
                        <div className="whitespace-pre-wrap text-slate-800 mt-0.5 leading-relaxed">{c.general_notes}</div>
                      </div>
                    )}
                    {c.triggered_promotion && (
                      <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                        This call triggered a promotion: <span className="font-semibold">{c.triggered_promotion.from}</span> → <span className="font-semibold">{c.triggered_promotion.to}</span> by {c.triggered_promotion.by_email}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
